import {checkIfTransactionEntryLineEmpty, deleteLine, newEntryLine, useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import {nextTick} from 'vue'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {InvoiceDraft} from '@/invoice/types/InvoiceDraft'
import {transactionManagerDocumentUrls, fileUpload, storageGetFile, resolveFileIcon, transactionManagerDocumentUrlsRevoke} from '@/services/document.service'
import {IncomingDocument} from '@/types/IncomingDocument'
import {contentExistInPanels} from '@tritt/multipanel'
import {focusGridElement} from '@/transaction/create-transaction/focusHelper'
import {createSubAccountFromOrgNo, findSupplierByOrgNo} from '@/subaccount/subAccount.service'
import {SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {findAccountByNo} from '@/bookkeeping-account/bookkeepingAccount.service'
import {getDocumentMeta} from '@/services/infrastructure/storage.service'
import {CREATE_TRANSACTION} from '@/transaction/create-transaction/index'
import {SUPPLIER} from '@/subaccount/types/SubAccount'
import {useConfirm} from 'primevue'
import {notifyError, notifySuccess} from '@/services/infrastructure/notification.service'

export const useAttachDocumentService = () => {

    const {attachments, transactionEntryLines} = useTransactionCreator()
    const confirm = useConfirm()

    const isDocumentAttached = (uuid: string) => {
        return attachments.value.some(a => a.uuid === uuid)
    }

    const updateDocumentFileMap = async (documentMeta: DocumentMeta) => {
        const documentUuid = documentMeta.uuid
        if (!transactionManagerDocumentUrls.value.has(documentUuid)) {
            const file = await storageGetFile(documentMeta)
            transactionManagerDocumentUrls.value.set(documentUuid, URL.createObjectURL(file))
        }
    }

    const attachDocument = async (incomingDocument: IncomingDocument) => {
        if (!contentExistInPanels(CREATE_TRANSACTION.componentName)) {
            return
        }

        attachments.value.push(incomingDocument.documentMeta)
        if (canBeInterpreted(incomingDocument)) {
            await handleInvoiceDraft(incomingDocument.invoiceDraft)
        }
        await updateDocumentFileMap(incomingDocument.documentMeta)

        nextTick(() => {
            focusGridElement(0, 0)
        }).then()

    }

    const handleInvoiceDraft = async (invoiceDraft: InvoiceDraft) => {
        // TODO move to invoice draft service and handle product lines
        const line = newEntryLine()
        if (invoiceDraft.draft.sender?.orgNo) {
            let supplier = findSupplierByOrgNo(invoiceDraft.draft.sender.orgNo)
            if (!supplier) {
                supplier = await new Promise((resolve: Function) => {
                    confirm.require({
                        header: 'Mangler leverandør',
                        message: `Leverandør ${invoiceDraft.draft.sender!.name}/${invoiceDraft.draft.sender!.orgNo} er ikke registrert i systemet. Ønsker du å opprette en ny leverandør?`,
                        accept: () => {
                            createSubAccountFromOrgNo(
                                invoiceDraft.draft.sender!.orgNo!,
                                SUPPLIER
                            ).then((newSupplier) => {
                                notifySuccess({title: 'Leverandør opprettet', description: `Leverandør ${newSupplier?.name} (${newSupplier?.orgNo}) er opprettet.`})
                                resolve(newSupplier)
                            })
                        },
                        reject: () => resolve()
                    })
                })
            }
            if (supplier) {
                line.creditSubAccount = supplier.uuid
                line.creditAccount = SUPPLIER_ACCOUNT_NO
                const suggestions = supplier.metadata?.suggestions
                if (suggestions) {
                    if(suggestions.expenseAccount) {
                        line.debitAccount = suggestions.expenseAccount
                        const debitAccount = findAccountByNo(suggestions.expenseAccount)
                        //If debitAccount has a suggested VAT, set this also
                        if (debitAccount?.vatSuggestionId) {
                            line.vatOptionIdDebit = debitAccount.vatSuggestionId
                        }
                    }
                    if(suggestions.project) line.project = suggestions.project
                    if(suggestions.department) line.department = suggestions.department

                }
            }
        }

        if (invoiceDraft.draft.invoiceNo) {
            line.invoiceNo = invoiceDraft.draft.invoiceNo
        }

        if (invoiceDraft.draft.grossAmount) {
            line.amount = invoiceDraft.draft.grossAmount
        }

        if (invoiceDraft.draft.date) {
            line.date = new Date(invoiceDraft.draft.date)
        }

        if (invoiceDraft.draft.dueDate) {
            line.invoiceDueDate = new Date(invoiceDraft.draft.dueDate)
        }

        if (invoiceDraft.draft.customerIdentification) {
            line.kid = invoiceDraft.draft.customerIdentification
        }

        if (invoiceDraft.draft.bankAccount) {
            line.bban = invoiceDraft.draft.bankAccount
        }

        // TODO if first element then downstream props dont update, fix this
        if (checkIfTransactionEntryLineEmpty(transactionEntryLines.value[0])) {
            transactionEntryLines.value = [line]
        } else {
            transactionEntryLines.value.push(line)
        }
    }

    const detachDocument = (incomingDocument: IncomingDocument) => {
        attachments.value = attachments.value.filter(attachment => attachment.uuid !== incomingDocument.documentMeta.uuid)

        if (canBeInterpreted(incomingDocument)) {
            const line = transactionEntryLines.value.find(line => line.invoiceNo === incomingDocument.invoiceDraft.draft.invoiceNo)
            if (line) {
                deleteLine(line)
            }
        }
        transactionManagerDocumentUrlsRevoke([incomingDocument.documentMeta])
    }

    const detachDocuments = (incomingDocuments: IncomingDocument[]) => {
       incomingDocuments.forEach(detachDocument)
    }

    const canBeInterpreted = (incomingDocument: IncomingDocument) => {
        return Object.keys(incomingDocument.invoiceDraft.draft).length !== 0
    }

    const toggleAttachDocument = async (incomingDocumentMeta: IncomingDocument) => {
        if (!contentExistInPanels(CREATE_TRANSACTION.componentName)) return

        if (!isDocumentAttached(incomingDocumentMeta.documentMeta.uuid)) {
            await attachDocument(incomingDocumentMeta)
        } else {
            detachDocument(incomingDocumentMeta)
        }
    }

    const copyAndAttachDocumentsToTransaction = async (documentMetaUuids: string[] | undefined) => {
        attachments.value = []
        if (documentMetaUuids && documentMetaUuids.length > 0) {
            const documents: DocumentMeta[] = []
            await Promise.all(documentMetaUuids.map(async uuid => {
                try {
                    const documentMeta = await getDocumentMeta(uuid)
                    if (documentMeta) {
                        const file = await storageGetFile(documentMeta)
                        const newDocumentMeta = await fileUpload(file)
                        documents.push(newDocumentMeta)
                    }
                } catch (error: any) {
                    notifyError({title: 'Feilet', description: {descriptionMessage: `Klarte ikke å hente dokument med id: ${uuid}`, error: error}})
                    console.error(`Error fetching document for UUID ${uuid}:`, error)
                }
            }))
            attachments.value = documents.map(doc => ({...doc, fileIcon: resolveFileIcon(doc.filename)}))
        }
    }

    const copyAndAttachUploadedDocumentsToTransaction = async (documentMetaUuids: string[] | undefined) => {
        attachments.value = []
        if (documentMetaUuids && documentMetaUuids.length > 0) {
            const documents: DocumentMeta[] = []
            await Promise.all(documentMetaUuids.map(async uuid => {
                try {
                    const documentMeta = await getDocumentMeta(uuid)
                    if (documentMeta) {
                        documents.push(documentMeta)
                    }
                } catch (error: any) {
                    notifyError({title: 'Feilet', description: {descriptionMessage: `Klarte ikke å hente dokument med id: ${uuid}`, error: error}})
                }
            }))
            attachments.value = documents.map(doc => ({...doc, fileIcon: resolveFileIcon(doc.filename)}))
        }
    }

    return {
        copyAndAttachDocumentsToTransaction,
        copyAndAttachUploadedDocumentsToTransaction,
        toggleAttachDocument,
        isDocumentAttached,
        detachDocument,
        detachDocuments,
        attachDocument
    }
}